import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { NewInvitationContent } from '../components/new-invitation/NewInvitationContent';
import {
  useAcceptInvitation,
  useAcceptInvitationPhoenix,
  useGetNursingHomeList,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NewInvitationPageProps {}

export const NewInvitationPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');

  const phoenixApiEnabled = useFeatureFlagEnabled(
    FeatureFlag.dev_ServiceProviderPhoenixApi
  );
  const { mutateAsync: acceptInvitation } = useAcceptInvitation();
  const { mutateAsync: acceptInvitationPhoenix } = useAcceptInvitationPhoenix();

  const { data: nursingHomeList } = useGetNursingHomeList();

  if (!tenant || !globalServiceProviderId || !nursingHomeId) {
    throw new Error('Missing required parameters for invitation');
  }

  // The nursing home is not there if a) the nursing home does not exist or b) the invitation is not valid anymore
  const nursingHome = useMemo(() => {
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const onAcceptInvitation = useCallback(async () => {
    if (nursingHome) {
      if (phoenixApiEnabled) {
        await acceptInvitationPhoenix({
          nursing_home_id: nursingHomeId as string,
        });
      } else {
        await acceptInvitation({
          nursingHomeId: nursingHome.nursingHomeId,
          globalServiceProviderId,
          tenant,
        });
      }
      enqueueSnackbar(
        t('service-provider-app.new-invitation.invitation-accepted'),
        { variant: 'success' }
      );
    } else {
      // If the nursing home is not there, it either does not exist (unlikely) or the invitation is not valid anymore.
    }
    navigate(spAppMainPages.HOME);
  }, [
    nursingHome,
    navigate,
    phoenixApiEnabled,
    enqueueSnackbar,
    t,
    acceptInvitationPhoenix,
    nursingHomeId,
    acceptInvitation,
    globalServiceProviderId,
    tenant,
  ]);

  return (
    <NewInvitationContent
      accountAffiliationName={nursingHome?.nursingHomeName}
      connectionStatus={nursingHome?.connectionStatus}
      onAcceptInvitation={onAcceptInvitation}
    />
  );
};
